<template>
  <div class="question-form">
    <LeadInput
      v-model="editableQuestion.title"
      :label="$t('form.questions.question_title')"
      :aria-label="$t('form.questions.question_title')"
      :placeholder="$t('form.questions.title_placeholder')"
      :errors="errors?.name || errors?.label"
      :is-required="true" />
    <slot name="ai" />
    <!-- lithium's textarea needs an explicit value binding to display loaded and generated text -->
    <LeadInput
      v-model="editableQuestion.text"
      :value="editableQuestion.text"
      :label="$t('form.questions.question_text')"
      :aria-label="$t('form.questions.question_text')"
      type="textarea"
      :rows="3"
      :errors="errors?.description" />

    <div>
      <div class="field-label">{{ $t('form.questions.answer_type') }}</div>
      <LeadSegmentedControl
        v-model="editableQuestion.type"
        :label="$t('form.questions.answer_type')"
        :options="questionTypes"
        class="question-type-selector" />
      <p class="field-hint">{{ $t(`form.questions.type_${editableQuestion.type}_hint`) }}</p>
    </div>

    <div v-if="editableQuestion.type !== 'text'" class="answers-section">
      <div class="field-label">{{ $t('form.questions.answers') }}</div>
      <div v-for="(answer, answerIndex) in editableQuestion.answers" :key="answer.id" class="answer-item">
        <span class="answer-number" aria-hidden="true">{{ answerIndex + 1 }}</span>
        <LeadInput
          v-model="answer.label"
          :aria-label="`${$t('form.questions.answer')} ${answerIndex + 1}`"
          :placeholder="`${$t('form.questions.answer')} ${answerIndex + 1}`"
          :errors="errors?.[`options.choices.${answerIndex}.label`]"
          class="answer-input" />
        <LeadButton
          variant="icon"
          class="remove-answer"
          :aria-label="`${$t('form.questions.remove_answer')} ${answerIndex + 1}`"
          @click="removeAnswer(answerIndex)">
          <IconX :size="16" />
        </LeadButton>
      </div>
      <FormErrorList v-if="errors?.options" :errors="errors.options" class="text-danger" />
      <LeadButton variant="white" class="add-answer" :icon-start="IconPlus" @click="addAnswer">
        {{ $t('form.questions.add_answer') }}
      </LeadButton>
      <LeadInput
        v-if="editableQuestion.type === 'single'"
        v-model="editableQuestion.is_dropdown"
        type="switch"
        :text="$t('form.questions.dropdown_hint')"
        class="dropdown-option" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { IconPlus, IconX } from '@tabler/icons-vue';
import type { UiQuestion } from '@/types/formQuestion';
import LeadInput from '../form/LeadInput.vue';
import LeadSegmentedControl from '../form/LeadSegmentedControl.vue';
import LeadButton from '../generic/LeadButton.vue';
import translator from '@/locale/translator';
import { FormErrorList } from '@prospective/lithium';

interface Props {
  errors?: Record<string, string[]> | null;
}

defineProps<Props>();
const editableQuestion = defineModel<UiQuestion>({ required: true });

/* -------------------------------- COMPUTED -------------------------------- */
const questionTypes = computed(() => [
  { value: 'single' as const, label: translator.get('form.questions.type_single') },
  { value: 'multiple' as const, label: translator.get('form.questions.type_multiple') },
  { value: 'text' as const, label: translator.get('form.questions.type_text') },
]);

/* --------------------------------- METHODS -------------------------------- */
function addAnswer() {
  editableQuestion.value.answers ??= [];
  editableQuestion.value.answers.push({ id: crypto.randomUUID(), label: '' });
}

function removeAnswer(index: number) {
  editableQuestion.value.answers?.splice(index, 1);
}
</script>

<style scoped>
.question-form {
  display: grid;
  gap: 20px;
}

.field-label {
  margin-bottom: 8px;
  color: #344054;
  font-size: 13px;
  font-weight: 500;
}

.field-hint {
  margin: 8px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.question-type-selector {
  display: flex;
  flex-wrap: wrap;
}

.question-type-selector :deep(.segment) {
  flex: 1 0 auto;
  height: auto;
  min-height: 36px;
  padding: 6px 10px;
  white-space: nowrap;
}

.answer-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.answer-number {
  width: 14px;
  padding-top: 10px;
  color: #98a2b3;
  font-size: 12px;
  flex-shrink: 0;
}

.answer-input {
  flex: 1;
  min-width: 0;
}

.remove-answer.lead-button {
  padding: 8px;
  border: 0;
  box-shadow: none;
  color: #667085;
}

.remove-answer:hover {
  color: #d92d20;
  background: #fef3f2;
}

.add-answer {
  width: 100%;
  border-style: dashed;
  background: #fff;
}

.dropdown-option {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eaecf0;
}
</style>
